import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { sanitizeBreadcrumb } from './app/utils/sentry-breadcrumb';
import { ConsentGateError } from './app/consent/consent';

if (environment.production) {
  enableProdMode();
}

if (environment.sentry.dsn) {
  try {
    Sentry.init({
      dsn: environment.sentry.dsn,
      tracesSampleRate: environment.sentry.tracesSampleRate,
      tracePropagationTargets: [/\.igive\.(no|xyz)$/],
      // Sentry is ungated and un-deferred: essential for service delivery, sets no
      // cookie, and is the only thing that would report the consent gate itself
      // failing. sendDefaultPii is set explicitly false regardless.
      sendDefaultPii: false,
      beforeBreadcrumb: sanitizeBreadcrumb,
      // Mobile Safari iOS 18+ throws this on cross-origin frames. It was added
      // for Sleeknote, which is gone from GTM-MGWBX2K as of 2026-08-18, but the
      // app embeds minegavekort.app gift-reveal previews in distribution and in
      // the card preview modal, so the error class has a live first-party source
      // and the filter stays. The denyUrls list beside it did not: all four of
      // its patterns were Sleeknote's own bundles.
      ignoreErrors: [
        /Blocked a frame with origin .* from accessing a cross-origin frame/,
      ],
      integrations: [
        Sentry.browserTracingIntegration(),
      ],
    });
    drainConsentGateErrors();
  } catch (err) {
    console.error('Sentry init failed, continuing without error tracking:', err);
  }
}

/**
 * Hands Sentry the failures the consent gate had to swallow.
 *
 * The gate runs while <head> is parsed, so Sentry's handlers do not exist yet
 * and nothing it throws could be captured even if it were rethrown. It buffers
 * into window.__igiveConsentGateErrors instead; this drains what accumulated
 * before init, then replaces the buffer with a live sink so the click-time
 * failures — a withdrawal that could not clear its cookies, a decision that
 * could not be stored — report as they happen rather than piling up unread.
 *
 * Only ever called inside the DSN guard, so on staging and in dev the buffer
 * stays a bounded array that nobody reads, which is why it has a size cap.
 */
function drainConsentGateErrors(): void {
  const report = (entry: ConsentGateError): void => {
    Sentry.captureException(
      entry.error instanceof Error ? entry.error : new Error(String(entry.error)),
      { tags: { mechanism: 'consent-gate', stage: entry.tag } }
    );
  };

  const existing = window.__igiveConsentGateErrors;
  const buffered: ConsentGateError[] = Array.isArray(existing) ? existing : [];

  window.__igiveConsentGateErrors = {
    push: (entry: ConsentGateError) => {
      report(entry);
      return 0;
    },
  };

  buffered.forEach((entry) => {
    try {
      report(entry);
    } catch {
      // Reporting a swallowed failure must not itself break bootstrap.
    }
  });
}

platformBrowserDynamic().bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()] })
  .catch(err => console.log(err));
